<template>
  <v-container fluid class="profile-container">
    <v-card v-if="userReady" class="profile-card">
      <!-- Colored header remains intact -->
      <v-card-title :class="[
          'profile-card-header', 
          employeeHeaderClass, 
          'text-center', 
          'text-h5', 
          'font-weight-bold'
        ]">
        {{ user.bar || 'N/A' }} staff
      </v-card-title>

      <!-- Top divider -->
      <v-divider class="custom-divider"></v-divider>

      <!-- Profile Content Section -->
      <v-card-text class="pt-5 pb-2">
        <div class="content-wrapper">
          <!-- Profile Picture with revolving ring -->
          <div class="profile-picture-wrapper">
            <img :src="user.bar_logo_url || '/static/images/logos/default.png'" alt="Bar Logo Ring"
              class="rotating-logo-ring" @error="useDefaultBarLogo" />
            <div class="avatar">
              <v-avatar size="140">
                <img :src="user.profile_picture_url || '/media/profile_pictures/default_profile.png'"
                  alt="Profile Picture" class="profile-img" loading="lazy" :style="profileImgStyle"
                  @error="useDefaultProfilePicture" />
              </v-avatar>
            </div>
          </div>
          <!-- Profile Info -->
          <div class="profile-info text-center mb-4">
            <div>
              <strong>{{ user.first_name || 'N/A' }} {{ user.last_name || 'N/A' }}</strong>
            </div>
            <div>
              <strong>{{ displayRole }}</strong>
            </div>
            <div>
              <strong>{{ user.birthday ? formatBirthday(user.birthday) : 'N/A' }}</strong>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Divider above Persistent Discounts Section -->
      <v-divider v-if="user.staff_discount > 0 || user.extra_discount"
        class="custom-divider"></v-divider>

      <!-- Persistent Discounts Section: adjusted layout -->
      <v-card-text class="discounts-section text-center">
        <div class="discount-inner">
          <p v-if="user.staff_discount > 0" class="discount-header">
            Staff discount:
            <span class="text-danger">{{ user.staff_discount }}%</span>
          </p>
          <p v-if="user.extra_discount" class="discount-header extra-discount-text">
            Extra discount: {{ user.extra_discount }}
          </p>
        </div>
      </v-card-text>

      <v-divider class="custom-divider"></v-divider>
    </v-card>
    <!-- Primary Wallet action -->
    <v-card-actions class="justify-center wallet-primary-trigger">
      <v-btn class="wallet-primary-btn" @click="$router.push('/wallet')">
        <SvgIcon :path="mdiWalletOutline" size="18" class="wallet-primary-icon" />
        <span>Open Wallet</span>
      </v-btn>
    </v-card-actions>

    <!-- Profile actions -->
    <v-card-actions class="justify-center terms-trigger">
      <v-btn text small class="terms-btn" @click="$router.push('/profile/edit')">
        Edit Profile
      </v-btn>
    </v-card-actions>

    <!-- Floating Terms & Conditions button -->
    <v-card-actions class="justify-center terms-fixed-trigger">
      <v-btn text small class="terms-btn" @click="showTerms = true">
        Terms &amp; Conditions
      </v-btn>
    </v-card-actions>

    <v-card-actions class="justify-center delete-trigger">
      <v-btn text small class="delete-btn" @click="openDeleteDialog">
        Delete Account
      </v-btn>
    </v-card-actions>

    <!-- Terms & Conditions Dialog -->
    <v-dialog v-model="showTerms" scrollable max-width="600" transition="dialog-bottom-transition">
      <v-card class="mx-auto">
        <v-card-title class="headline text-center">Terms &amp; Conditions</v-card-title>
        <v-divider />
        <v-card-text class="pa-4" style="max-height: 60vh; overflow-y: auto;">
          <div class="terms-content" v-html="termsContent"></div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showTerms = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showDeleteDialog" max-width="480">
      <v-card>
        <v-card-title class="text-h6">Delete Account</v-card-title>
        <v-card-text>
          <p class="mb-4">
            This permanently deletes your account and signs you out. Historical operational records are kept without your personal account data. Audit and security logs may retain identifying information and request metadata, such as IP address, about actions taken in the app for up to 180 days before they are permanently deleted.
          </p>
          <v-text-field
            v-model="deletePassword"
            label="Current Password"
            type="password"
            autocomplete="current-password"
            :disabled="isDeleting"
            @input="deleteError = ''"
          />
          <v-alert
            v-if="deleteError"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mt-2"
          >
            {{ deleteError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="isDeleting" @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="isDeleting"
            :disabled="isDeleting || !deletePassword.trim()"
            @click="confirmDeleteAccount"
          >
            Permanently Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-alert v-if="error" type="error" prominent>
      Unable to load profile information. Please try again later.
    </v-alert>
  </v-container>
</template>

<script>
import axios from "axios";
import { mdiWalletOutline } from "@mdi/js";
import { useUserStore } from "@/stores/user";
export default {
  name: "ProfilePage",
  data() {
    return {
      userStore: null,
      user: {},
      userReady: false,
      error: false,
      showTerms: false,
      termsContent: '',
      mdiWalletOutline,
      showDeleteDialog: false,
      deletePassword: "",
      deleteError: "",
      isDeleting: false,
    };
  },
  async created() {
    this.userStore = useUserStore();
    const p1 = this.fetchProfile();
    const p2 = this.fetchTerms();
    await Promise.all([p1, p2]);
  },
  methods: {
    async fetchProfile() {
      try {
        const response = await axios.get("/api/user/", { withCredentials: true });
        if (response.data) {
          this.user = {
            username: response.data.username || "",
            first_name: response.data.first_name || "",
            last_name: response.data.last_name || "",
            bar: response.data.bar || "",
            role: response.data.role || "",
            role_pretty: response.data.role_pretty || "",
            birthday: response.data.birthday || "",
            profile_picture_url: response.data.profile_picture_url || "",
            bar_logo_url: response.data.bar_logo_url || "",
            staff_discount: response.data.staff_discount || 0,
            extra_discount: response.data.extra_discount || "",
            is_vip: response.data.is_vip || false,
          };
          this.userReady = true;
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        this.error = true;
      }
    },
    async fetchTerms() {
      try {
        const resp = await axios.get('/api/terms-and-conditions/', { withCredentials: true });
        this.termsContent = resp.data.content || '';
      } catch (e) {
        console.error('Error fetching terms:', e);
      }
    },
    formatBirthday(date) {
      if (!date) return "N/A";
      const dayToken = String(date).slice(0, 10);
      const parts = dayToken.split("-");
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return String(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    },
    useDefaultProfilePicture(e) {
      e.target.src = "/media/profile_pictures/default_profile.png";
    },
    useDefaultBarLogo(e) {
      e.target.src = "/static/images/logos/default.png";
    },
    openDeleteDialog() {
      this.deletePassword = "";
      this.deleteError = "";
      this.showDeleteDialog = true;
    },
    closeDeleteDialog() {
      if (this.isDeleting) return;
      this.showDeleteDialog = false;
      this.deletePassword = "";
      this.deleteError = "";
    },
    async confirmDeleteAccount() {
      if (this.isDeleting) return;
      const password = this.deletePassword.trim();
      if (!password) {
        this.deleteError = "Current password is required.";
        return;
      }

      this.isDeleting = true;
      this.deleteError = "";

      try {
        await this.userStore.deleteAccount(password);
      } catch (error) {
        const data = error?.response?.data || {};
        if (Array.isArray(data.password) && data.password.length) {
          this.deleteError = data.password[0];
        } else if (typeof data.detail === "string" && data.detail) {
          this.deleteError = data.detail;
        } else if (typeof data.error === "string" && data.error) {
          this.deleteError = data.error;
        } else {
          this.deleteError = "Failed to delete account. Please try again.";
        }
      } finally {
        this.isDeleting = false;
      }
    },
  },
  computed: {
    displayRole() {
      return this.user.role === 'supermanager'
        ? 'Management'
        : (this.user.role_pretty || 'N/A');
    },
    employeeHeaderClass() {
      if (!this.user || !this.user.bar) return "";
      if (this.user.bar === "Temple Bar") return "header-temple";
      else if (this.user.bar === "Stefanshus") return "header-stefanshus";
      else if (this.user.bar === "Klovnen") return "header-klovnen";
      else if (this.user.bar === "Understellet") return "header-understellet";
      else if (this.user.bar === "Management") return "header-management";
      return "";
    },
    profileImgStyle() {
      if (!this.user || !this.user.bar) return {};
      if (this.user.is_vip) {
        return { border: "3px solid #ffd700" };
      } else {
        if (this.user.bar === "Temple Bar") return { border: "3px solid #541B21" };
        else if (this.user.bar === "Stefanshus") return { border: "3px solid #156273" };
        else if (this.user.bar === "Klovnen") return { border: "3px solid #E47C25" };
        else if (this.user.bar === "Understellet") return { border: "3px solid #00A287" };
        else if (this.user.bar === "Management") return { border: "3px solid #0d0d0d" };
        return { border: "3px solid #1976d2" };
      }
    },
  }
};
</script>

<style scoped>
/* General Profile Card Styling */
.profile-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  min-height: 100vh;
  padding: 30px 20px;
}
.profile-card {
  max-width: 500px;
  margin: 10px auto 0 auto;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}
.profile-card-header {
  font-size: 18px;
  font-weight: bold;
  padding: 10px;
  color: #fff;
}
.header-temple {
  background-color: #541B21;
}
.header-stefanshus {
  background-color: #156273;
}
.header-klovnen {
  background-color: #E47C25;
}
.header-understellet {
  background-color: #00A287;
}
.header-management {
  background-color: #0d0d0d;
}
.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.profile-picture-wrapper {
  position: relative;
  width: 170px;
  height: 170px;
  margin: 5px auto 20px auto;
}
.rotating-logo-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 170px;
  height: 170px;
  object-fit: contain;
  animation: rotate 5s linear infinite;
  z-index: 0;
}
.avatar {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 1;
}
.profile-info {
  font-size: 1.1rem;
}

/* Adjusted Persistent Discounts Section */
.discounts-section {
  padding-top: 20px;
  padding-bottom: 20px;
  display: flex;
  justify-content: center;
}
.discount-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
}
/* Remove auto margins on first and last headers */
.discounts-section .discount-header:first-of-type {
  margin-top: 0;
}
.discounts-section .discount-header:last-of-type {
  margin-bottom: 0;
}
/* Base discount header style */
.discount-header {
  font-size: 1.1rem;
  font-weight: bold;
  margin: 0.3rem 0; /* This controls the gap between the discount lines */
  color: #333;
}
.extra-discount-text {
  color: #00796b;
}

/* The bottom divider can be adjusted */
.bottom-divider {
  margin-top: 0rem !important;
  margin-bottom: 1rem !important;
}

/* One-Time Discounts */
.discount-group-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.discount-group {
  width: 100%;
}
.bar-header {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0.4rem;
}
.discount-item {
  margin-bottom: 0.6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.one-time-discount-text {
  font-size: 1.1rem;
  font-weight: normal;
  color: black;
  margin-bottom: 0.4rem;
}
/* Dash separator styling between discounts */
.dash-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.4rem 0;
}
.custom-dash {
  font-size: 1.2rem;
  display: inline-block;
  transform: translateY(9px);
  color: rgba(0, 0, 0, 0.7);
}

/* Star separator styling */
.custom-star {
  color: rgba(0, 0, 0, 0.7) !important;
}

/* Group separator between discount groups */
.group-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 10px 0;
}
.text-temple {
  color: #541B21;
}
.text-stefanshus {
  color: #156273;
}
.text-klovnen {
  color: #E47C25;
}
.text-understellet {
  color: #00A287;
}
.text-management {
  color: #0d0d0d;
}
.custom-divider {
  width: 80%;
  margin: 0 auto;
}
.profile-img {
  width: 140px;
  height: 140px;
  object-fit: cover;
  border-radius: 50%;
}
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.one-time-discounts .discount-header {
  font-size: 1.1rem;
  font-weight: bold;
  margin-top: 0;
  margin-bottom: 0.8rem;
}

/* Limit the width of the T&C button wrapper to match the card */
.terms-trigger {
  max-width: 500px;
  width: 100% !important;
  margin: 0 auto;
  padding: 0 !important;
  min-height: 0 !important;
  display: flex !important;
  justify-content: center !important;
}

.delete-trigger {
  max-width: 500px;
  width: 100% !important;
  margin: 0 auto;
  padding: 0 !important;
  min-height: 0 !important;
  display: flex !important;
  justify-content: center !important;
}

/* Center lists and add spacing inside Terms & Conditions HTML */
:deep(.terms-content ul) {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}
:deep(.terms-content li) {
  margin-bottom: 0.5rem;
}

/* Limit bottom-sheet width, center it, and restore white background */
:deep(.v-overlay__content .v-bottom-sheet__sheet) {
  max-width: 600px !important;
  margin: 0 auto !important;
  background-color: white !important;
  border-radius: 8px !important;
}

/* Center icon + text inside the button */
.terms-btn {
  width: auto !important;
  min-width: 0 !important;
  margin: 0 auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0.5rem 1rem !important;
}

.delete-btn {
  width: auto !important;
  min-width: 0 !important;
  margin: 0 auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0.5rem 1rem !important;
  color: #c62828 !important;
}

.wallet-primary-trigger {
  max-width: 500px;
  width: 100% !important;
  margin: 4px auto 0;
  padding: 0 !important;
  display: flex !important;
  justify-content: center !important;
}

.wallet-primary-btn {
  min-width: 220px !important;
  border-radius: 999px !important;
  padding: 0 18px !important;
  height: 42px !important;
  background: linear-gradient(135deg, #131826 0%, #20293f 100%) !important;
  color: #f6f8fc !important;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 700 !important;
  box-shadow: 0 8px 18px rgba(21, 28, 43, 0.22);
  transition: transform 150ms ease, box-shadow 150ms ease, filter 150ms ease;
}

.wallet-primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(21, 28, 43, 0.28);
  filter: brightness(1.03);
}

.wallet-primary-icon {
  margin-right: 8px;
  opacity: 0.95;
}

/* Floating Terms & Conditions button wrapper */
.terms-fixed-trigger {
  max-width: 500px;
  margin: 0 auto 0;
  padding: 0;
  min-height: 0 !important;
  display: flex;
  justify-content: center;
}
</style>
